import { Fragment, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '~/components/ui/buttons/Button';
import { Input } from '~/components/ui/inputs/Input';
import { Select } from '~/components/ui/inputs/Select';
import { SectionPanel } from '~/components/ui/SectionPanel';
import {
  statusBadgeClasses,
  type StatusBadgeTone,
} from '~/components/ui/statusBadgeClasses';
import {
  Table,
  useTable,
  type TableColumn,
  type TableRow,
} from '~/components/ui/table';
import {
  useEmailDeliveryTracesPaginated,
  useSendPlatformTestEmail,
} from '~/api/emailDelivery';
import { EmailDeliveryOutcome } from '~/graphql/graphql';
import type {
  EmailDeliveryTraceFilter,
  EmailDeliveryTracesQuery,
} from '~/graphql/graphql';
import { GraphQLRequestError } from '~/lib/GraphQLRequestError';
import { formatDateTime } from '~/utils';

const PAGE_SIZE = 20;

const QUOTA_EXCEEDED_CODE = 'test-email-quota-exceeded';

type EmailDeliveryTrace =
  EmailDeliveryTracesQuery['emailDeliveryTraces']['content'][number];

type TestSendResult =
  | { kind: 'idle' }
  | { kind: 'success'; sesMessageId: string | null }
  | { kind: 'quota'; retryAfterSeconds: number | null }
  | { kind: 'error'; message: string };

const OUTCOME_TONES: Record<EmailDeliveryOutcome, StatusBadgeTone> = {
  PENDING: 'neutral',
  DELIVERED: 'accepted',
  BOUNCED: 'declined',
  COMPLAINED: 'warning',
  REJECTED: 'declined',
  DELAYED: 'pending',
};

const IDLE_TEST_SEND_RESULT: TestSendResult = { kind: 'idle' };

const toQuotaRefusal = (error: Error): TestSendResult | null => {
  if (!(error instanceof GraphQLRequestError)) return null;

  const refusal = error.errors.find(
    (graphqlError) => graphqlError.extensions?.code === QUOTA_EXCEEDED_CODE
  );
  if (!refusal) return null;

  const retryAfterSeconds = refusal.extensions?.retryAfterSeconds;
  return {
    kind: 'quota',
    retryAfterSeconds:
      typeof retryAfterSeconds === 'number' ? retryAfterSeconds : null,
  };
};

const startOfDayIso = (day: string): string =>
  new Date(`${day}T00:00:00.000Z`).toISOString();

const endOfDayIso = (day: string): string =>
  new Date(`${day}T23:59:59.999Z`).toISOString();

const emptyDraft = {
  recipient: '',
  sesMessageId: '',
  deliveryOutcome: '',
  createdAfter: '',
  createdBefore: '',
};

type FilterDraft = typeof emptyDraft;

const toFilter = (draft: FilterDraft): EmailDeliveryTraceFilter | undefined => {
  const filter: EmailDeliveryTraceFilter = {};
  if (draft.recipient) filter.recipient = draft.recipient;
  if (draft.sesMessageId) filter.sesMessageId = draft.sesMessageId;
  if (draft.deliveryOutcome) {
    filter.deliveryOutcome = draft.deliveryOutcome as EmailDeliveryOutcome;
  }
  if (draft.createdAfter)
    filter.createdAfter = startOfDayIso(draft.createdAfter);
  if (draft.createdBefore)
    filter.createdBefore = endOfDayIso(draft.createdBefore);

  return Object.keys(filter).length > 0 ? filter : undefined;
};

export default function AdminEmailDelivery() {
  const { t } = useTranslation('email-delivery');

  const [recipient, setRecipient] = useState('');
  const [testSendResult, setTestSendResult] = useState<TestSendResult>(
    IDLE_TEST_SEND_RESULT
  );
  const [filterDraft, setFilterDraft] = useState<FilterDraft>(emptyDraft);
  const [appliedDraft, setAppliedDraft] = useState<FilterDraft>(emptyDraft);
  const [expandedTraceIds, setExpandedTraceIds] = useState<ReadonlySet<string>>(
    new Set()
  );

  const appliedFilter = useMemo(() => toFilter(appliedDraft), [appliedDraft]);

  const {
    data: traces,
    sentinelRef,
    isLoading,
    pagination,
  } = useEmailDeliveryTracesPaginated(PAGE_SIZE, appliedFilter);

  const { mutate: sendTestEmail, isPending: isSending } =
    useSendPlatformTestEmail();

  const handleSendTestEmail = () => {
    setTestSendResult(IDLE_TEST_SEND_RESULT);
    sendTestEmail(
      { input: { recipient } },
      {
        onSuccess: (data) =>
          setTestSendResult({
            kind: 'success',
            sesMessageId: data.sendPlatformTestEmail.sesMessageId ?? null,
          }),
        onError: (error) =>
          setTestSendResult(
            toQuotaRefusal(error) ?? { kind: 'error', message: error.message }
          ),
      }
    );
  };

  const toggleTrace = (traceId: string) =>
    setExpandedTraceIds((previous) => {
      const next = new Set(previous);
      if (next.has(traceId)) {
        next.delete(traceId);
      } else {
        next.add(traceId);
      }
      return next;
    });

  const updateDraft =
    (field: keyof FilterDraft) => (_: string, value: string) =>
      setFilterDraft((previous) => ({ ...previous, [field]: value }));

  const outcomeOptions = [
    { value: '', label: t('filters.outcomeAny') },
    ...Object.values(EmailDeliveryOutcome).map((outcome) => ({
      value: outcome,
      label: t(`outcome.${outcome}`),
    })),
  ];

  const columns: TableColumn[] = [
    { key: 'expand', header: '', width: 3 },
    { key: 'createdAt', header: t('table.createdAt'), width: 14 },
    { key: 'emailType', header: t('table.emailType'), width: 18 },
    { key: 'recipientTo', header: t('table.recipient'), width: 18 },
    { key: 'status', header: t('table.status'), width: 10 },
    { key: 'deliveryOutcome', header: t('table.outcome'), width: 12 },
    { key: 'sesMessageId', header: t('table.sesMessageId'), width: 20 },
  ];

  const buildTraceRow = (trace: EmailDeliveryTrace): TableRow => {
    const isExpanded = expandedTraceIds.has(trace.id);
    return {
      id: { value: trace.id },
      expand: {
        value: (
          <button
            type="button"
            onClick={() => toggleTrace(trace.id)}
            aria-expanded={isExpanded}
            aria-label={isExpanded ? t('table.collapse') : t('table.expand')}
            className="text-neut-sec hover:text-neut-prim"
            data-testid={`trace-expand-${trace.id}`}
          >
            {isExpanded ? (
              <ChevronDown size={16} aria-hidden />
            ) : (
              <ChevronRight size={16} aria-hidden />
            )}
          </button>
        ),
      },
      createdAt: { value: formatDateTime(trace.createdAt) },
      emailType: { value: trace.emailType },
      recipientTo: { value: trace.recipientTo },
      status: { value: trace.status },
      deliveryOutcome: {
        value: (
          <span
            className={statusBadgeClasses(OUTCOME_TONES[trace.deliveryOutcome])}
            data-testid={`outcome-badge-${trace.id}`}
          >
            {t(`outcome.${trace.deliveryOutcome}`)}
          </span>
        ),
      },
      sesMessageId: {
        value: (
          <span className="font-mono text-xs break-all">
            {trace.sesMessageId ?? t('table.noMessageId')}
          </span>
        ),
      },
    };
  };

  const buildDeliveryDetail = (trace: EmailDeliveryTrace) => {
    const entries: { label: string; value: string }[] = [
      {
        label: t('detail.sentAt'),
        value: trace.sentAt
          ? formatDateTime(trace.sentAt)
          : t('detail.notSent'),
      },
      {
        label: t('detail.deliveryOutcomeAt'),
        value: trace.deliveryOutcomeAt
          ? formatDateTime(trace.deliveryOutcomeAt)
          : t('detail.outcomePending'),
      },
    ];

    if (trace.recipientCc.length > 0) {
      entries.push({
        label: t('detail.recipientCc'),
        value: trace.recipientCc.join(', '),
      });
    }

    if (trace.lastError) {
      entries.push({ label: t('detail.lastError'), value: trace.lastError });
    }

    return (
      <div
        className="flex flex-col gap-2"
        data-testid={`trace-detail-${trace.id}`}
      >
        <h3 className="text-xs font-semibold uppercase tracking-wide text-neut-sec">
          {t('detail.title')}
        </h3>
        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
          {entries.map((entry) => (
            <Fragment key={entry.label}>
              <dt className="text-neut-sec">{entry.label}</dt>
              <dd className="text-neut-prim break-all">{entry.value}</dd>
            </Fragment>
          ))}
        </dl>
      </div>
    );
  };

  const buildEventsRow = (trace: EmailDeliveryTrace): TableRow => ({
    id: { value: `${trace.id}::events` },
    _detail: true,
    _testIdSuffix: `events-${trace.id}`,
    _fullWidth: (
      <div className="flex flex-col gap-4">
        {buildDeliveryDetail(trace)}
        <div
          className="flex flex-col gap-2"
          data-testid={`trace-events-${trace.id}`}
        >
          <h3 className="text-xs font-semibold uppercase tracking-wide text-neut-sec">
            {t('events.title')}
          </h3>
          {trace.events.length === 0 ? (
            <p className="text-sm text-neut-sec">{t('events.empty')}</p>
          ) : (
            <ol className="flex flex-col gap-2">
              {trace.events.map((event) => (
                <li key={event.id} className="text-sm text-neut-prim">
                  <div className="flex flex-wrap items-baseline gap-2">
                    <span className="font-medium">{event.eventType}</span>
                    {event.recipient ? (
                      <span className="text-neut-sec">{event.recipient}</span>
                    ) : null}
                    <span className="text-neut-sec">
                      {formatDateTime(event.occurredAt)}
                    </span>
                  </div>
                  {event.bounceType ? (
                    <div className="text-xs text-neut-sec">
                      {t('events.bounceType')}: {event.bounceType}
                    </div>
                  ) : null}
                  {event.bounceSubType ? (
                    <div className="text-xs text-neut-sec">
                      {t('events.bounceSubType')}: {event.bounceSubType}
                    </div>
                  ) : null}
                  {event.reason ? (
                    <div className="text-xs text-neut-sec">
                      {t('events.reason')}: {event.reason}
                    </div>
                  ) : null}
                  {event.diagnosticCode ? (
                    <div className="text-xs font-mono break-all text-neut-sec">
                      {t('events.diagnosticCode')}: {event.diagnosticCode}
                    </div>
                  ) : null}
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>
    ),
  });

  const rows: TableRow[] = traces.flatMap((trace) =>
    expandedTraceIds.has(trace.id)
      ? [buildTraceRow(trace), buildEventsRow(trace)]
      : [buildTraceRow(trace)]
  );

  const tableControls = useTable({});

  return (
    <div className="w-full flex flex-col gap-6">
      <div>
        <h1 className="text-xl font-semibold text-neut-prim">{t('title')}</h1>
        <p className="text-sm text-neut-sec mt-1">{t('subtitle')}</p>
      </div>

      <SectionPanel title={t('testSend.title')}>
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-72">
              <Input
                name="testEmailRecipient"
                label={t('testSend.recipientLabel')}
                placeholder={t('testSend.recipientPlaceholder')}
                type="email"
                value={recipient}
                onChange={(_, value) => setRecipient(value)}
                data-testid="test-email-recipient"
              />
            </div>
            <Button
              width="auto"
              loading={isSending}
              disabled={isSending || recipient.trim() === ''}
              onClick={handleSendTestEmail}
              data-testid="test-email-submit"
            >
              {isSending ? t('testSend.sending') : t('testSend.submit')}
            </Button>
          </div>

          {testSendResult.kind === 'success' ? (
            <div
              className="rounded-md bg-success-100 p-3 text-sm text-success-700"
              data-testid="test-email-success"
            >
              <p className="font-medium">{t('testSend.successTitle')}</p>
              <p className="font-mono break-all">
                {t('testSend.messageIdLabel')}:{' '}
                {testSendResult.sesMessageId ?? t('table.noMessageId')}
              </p>
            </div>
          ) : null}

          {testSendResult.kind === 'quota' ? (
            <div
              className="rounded-md bg-warning-100 p-3 text-sm text-warning-700"
              data-testid="test-email-quota"
            >
              <p className="font-medium">{t('testSend.quotaTitle')}</p>
              <p>
                {testSendResult.retryAfterSeconds === null
                  ? t('testSend.quotaBody')
                  : t('testSend.quotaRetryAfter', {
                      seconds: testSendResult.retryAfterSeconds,
                    })}
              </p>
            </div>
          ) : null}

          {testSendResult.kind === 'error' ? (
            <div
              className="rounded-md bg-error-100 p-3 text-sm text-error-600"
              data-testid="test-email-error"
            >
              <p className="font-medium">{t('testSend.errorTitle')}</p>
              <p>{testSendResult.message}</p>
            </div>
          ) : null}
        </div>
      </SectionPanel>

      <SectionPanel title={t('table.title')} bodyClassName="">
        <div className="flex flex-wrap items-end gap-3 p-4">
          <div className="w-56">
            <Input
              name="filterRecipient"
              label={t('filters.recipient')}
              placeholder={t('filters.recipientPlaceholder')}
              value={filterDraft.recipient}
              onChange={updateDraft('recipient')}
              data-testid="filter-recipient"
            />
          </div>
          <div className="w-56">
            <Input
              name="filterSesMessageId"
              label={t('filters.sesMessageId')}
              placeholder={t('filters.sesMessageIdPlaceholder')}
              value={filterDraft.sesMessageId}
              onChange={updateDraft('sesMessageId')}
              data-testid="filter-ses-message-id"
            />
          </div>
          <div className="w-48">
            <Select
              name="filterDeliveryOutcome"
              label={t('filters.outcome')}
              options={outcomeOptions}
              value={filterDraft.deliveryOutcome}
              onChange={updateDraft('deliveryOutcome')}
              data-testid="filter-delivery-outcome"
            />
          </div>
          <div className="w-44">
            <Input
              name="filterCreatedAfter"
              label={t('filters.createdAfter')}
              type="date"
              value={filterDraft.createdAfter}
              onChange={updateDraft('createdAfter')}
              data-testid="filter-created-after"
            />
          </div>
          <div className="w-44">
            <Input
              name="filterCreatedBefore"
              label={t('filters.createdBefore')}
              type="date"
              value={filterDraft.createdBefore}
              onChange={updateDraft('createdBefore')}
              data-testid="filter-created-before"
            />
          </div>
          <Button
            width="auto"
            onClick={() => setAppliedDraft(filterDraft)}
            data-testid="filter-apply"
          >
            {t('filters.apply')}
          </Button>
          <Button
            width="auto"
            variant="secondary"
            onClick={() => {
              setFilterDraft(emptyDraft);
              setAppliedDraft(emptyDraft);
            }}
            data-testid="filter-clear"
          >
            {t('filters.clear')}
          </Button>
        </div>

        {isLoading && traces.length === 0 ? (
          <div className="p-8 text-center text-neut-sec text-sm">
            {t('table.loading')}
          </div>
        ) : (
          <Table
            minWidth={90}
            rows={rows}
            columns={columns}
            tableControls={tableControls}
            emptyMessage={t('table.empty')}
            totalRowsCount={pagination.totalElements}
            name="email-delivery-traces"
          />
        )}
        <div ref={sentinelRef} />
      </SectionPanel>
    </div>
  );
}
